import * as React from "react";

import { LabeledControl } from "@replohq/lattice/blocks/labeled-control/LabeledControl";
import { Button } from "@replohq/lattice/components/button/Button";
import { Input } from "@replohq/lattice/components/input/Input";
import { userRouter } from "@v2/trpc/routes/user";
import { useForm, useWatch } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { isEmpty } from "replo-utils/lib/misc";
import { z } from "zod";

const emailSchema = z.email();

type FormValues = {
  email: string;
};

const InitResetPassword: React.FC = () => {
  const { mutate: initResetPasswordApi, isPending: isLoadingInitResetPass } =
    userRouter.initResetPassword.useMutation();
  const [resetPasswordEmail, setResetPasswordEmail] = React.useState<
    string | null
  >(null);
  const [searchParams] = useSearchParams();
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    clearErrors,
  } = useForm<FormValues>({
    defaultValues: {
      email: searchParams.get("email") ?? "",
    },
    mode: "onBlur",
  });

  const watchAllFields = useWatch({ control });

  if (resetPasswordEmail) {
    const email = resetPasswordEmail;
    return (
      <div className="text-placeholder text-sm">
        <div>
          We sent you an email to
          <span className="text-default font-medium pl-1">{email}</span> with a
          secret link to reset your password.
        </div>
        <div className="pt-4">
          <span>
            If you do not see the email within 5 minutes, please contact us at
          </span>
          <a
            href="mailto:support@replo.app"
            className="text-primary pl-1 hover:underline"
          >
            support@replo.app
          </a>
        </div>
      </div>
    );
  }

  const emailError = !isEmpty(watchAllFields.email)
    ? errors?.email?.message
    : undefined;

  const onSubmit = ({ email }: FormValues) => {
    setResetPasswordEmail(email);
    initResetPasswordApi(email);
  };

  return (
    <form
      className="no-scrollbar flex flex-1 flex-col gap-y-10"
      onSubmit={(values) => {
        void handleSubmit(onSubmit)(values);
      }}
    >
      <LabeledControl label="Email" error={emailError}>
        <Input
          invalid={Boolean(emailError)}
          autoComplete="off"
          placeholder="Your Work Email"
          {...register("email", {
            required: "Please enter a valid email address.",
            validate: (value) => {
              const parsedEmail = emailSchema.safeParse(value);
              return (
                parsedEmail.success || "Please enter a valid email address."
              );
            },
            // NOTE (Fran 2024-02-27): We should clear the errors when the user is typing
            onChange: () => clearErrors("email"),
          })}
          type="email"
          size="base"
        />
      </LabeledControl>
      <Button
        className="w-full"
        variant="primary"
        size="base"
        type="submit"
        loading={isLoadingInitResetPass}
      >
        Send Reset Email
      </Button>
    </form>
  );
};

export default InitResetPassword;
